import { computed, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ContainerLocationBadge } from '@modules/common/utils/container-location-badge';
import { type Application } from '@modules/common/types/application';
import { type AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import type { SearchLocationBadgeType } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { type Observable } from 'rxjs';
import { map, skip } from 'rxjs/operators';

/** Container metadata used by a header badge; colour is used only for calendars. */
interface NamedContainer {
  readonly title?: string;
  readonly color?: string;
}

/**
 * The one thing this badge asks of a cache. Spelled out rather than `Pick<BulkCache, 'get'>`, because
 * `get` is overloaded and `Pick` collapses an overload set to a single signature — which then accepts test
 * doubles that would not satisfy the real method.
 */
interface ContainerLookup<CONTAINER extends NamedContainer> {
  get(key: Signal<string | null>): Signal<CONTAINER | undefined>;
}

/**
 * How the badge learns whether it may show — exactly one of the two, never both, so there is no question
 * which gate decides. Most callers let the factory build it from a search plus `alsoWhen`; the mail form
 * hands in its own `shown`, because it gates a second branch of its own (the virtual
 * inbox/other-mail/sent/draft/scheduled statuses) and two copies of one rule would leave a reader guessing
 * which one governs.
 */
type BadgeGate =
  | {
      readonly search: Pick<AdvancedSearchService, 'isAdvancedSearchApplied'>;
      readonly application: Application;
      /** A second reason to show the badge, independent of the search: the item was opened from the hub. */
      readonly alsoWhen: Signal<boolean>;
      readonly shown?: never;
    }
  | {
      readonly shown: Signal<boolean>;
      readonly search?: never;
      readonly application?: never;
      readonly alsoWhen?: never;
    };

interface HeaderLocationBadgeInputs<CONTAINER extends NamedContainer> {
  /** `FullFormBaseComponent.stitchItemChanged` — fires on load and after each save. */
  readonly changed: Observable<void>;
  /** Reads the container uid off the *saved* item; called only after {@link changed} has fired. */
  readonly containerId: () => string | null | undefined;
  readonly cache: ContainerLookup<CONTAINER>;
  readonly badgeType: SearchLocationBadgeType;
}

type HeaderLocationBadgeConfig<CONTAINER extends NamedContainer> = HeaderLocationBadgeInputs<CONTAINER> & BadgeGate;

/**
 * The container badge for a full form's header: which project / calendar / notebook / folder the open item
 * is filed under.
 *
 * Shared by all five item forms so three non-obvious rules live in one place instead of five:
 *
 * - The uid comes from the *saved* item, not the form control, so the badge says where the item **is**;
 *   reassigning the container in the form moves it only once saved, which is when `changed` fires.
 * - `skip(1)` drops the `BehaviorSubject`'s seeded emission. It fires while the caller's fields are still
 *   initialising, before the item input exists — and on the task form `stitchItem` reads a *signal* input,
 *   so touching it that early throws instead of yielding undefined.
 * - The gate — `search.isAdvancedSearchApplied(application) || alsoWhen()`, or the caller's `shown()` — is
 *   checked before the container signal is read, so while it is off the cache is never watched and no bulk
 *   request goes out at all.
 *
 * Must be called from an injection context (a component field initialiser).
 */
export function headerLocationBadge<CONTAINER extends NamedContainer>(
  config: HeaderLocationBadgeConfig<CONTAINER>,
): Signal<ContainerLocationBadge | null> {
  const { changed, containerId, cache, badgeType } = config;
  const savedContainerId = toSignal(
    changed.pipe(
      skip(1),
      map(() => containerId() ?? null),
    ),
    { initialValue: null },
  );
  const container = cache.get(savedContainerId);
  /*
   * Eager on purpose. `toSignal` subscribes and writes a signal, so building the gate on first read would
   * mean doing that inside a `computed` — the same illegal write `BulkCache.schedule` is written to dodge,
   * and `{ injector }` does not change it. A lazy version would need a different mechanism, e.g. an
   * `effect` seeding a plain `signal`; that trades one subscription per open form for an effect per form,
   * which is not obviously cheaper.
   */
  let shown: () => boolean;
  if (config.shown) {
    shown = config.shown;
  } else {
    const { alsoWhen } = config;
    const searchApplied = toSignal(config.search.isAdvancedSearchApplied(config.application), { initialValue: false });
    shown = () => searchApplied() || alsoWhen();
  }

  return computed(() => {
    if (!shown()) {
      return null;
    }

    const name = container()?.title;

    if (!name) return null;
    return badgeType === 'calendar'
      ? { type: badgeType, name, color: container()?.color }
      : { type: badgeType, name };
  });
}
